
import { useState } from "react";
import { format } from "date-fns";
import { pt } from "date-fns/locale";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import { RequestScheduleDialog } from "./RequestScheduleDialog";
import { CancelScheduleDialog } from "./CancelScheduleDialog";
import { StudentEvent } from "./types";
import { StudentCalendarViews } from "./StudentCalendarViews";
import { useScheduleQueries } from "./hooks/useScheduleQueries";
import { useLanguage } from "@/components/LanguageContext";
import { useTranslations } from "@/hooks/useTranslations";
import { EventDetailsDialog } from "./components/EventDetailsDialog";
import { ScheduleSkeleton } from "@/components/shared/DashboardSkeleton";

export const StudentSchedule = () => {
  const [selectedDate, setSelectedDate] = useState<Date>();
  const [showRequestDialog, setShowRequestDialog] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<StudentEvent | null>(null);
  const [date, setDate] = useState(new Date());
  const [localEvents, setLocalEvents] = useState<StudentEvent[]>([]);
  
  const { language } = useLanguage();
  const { t } = useTranslations("schedule");
  
  const {
    student,
    settings,
    trainerEvents,
    isLoading,
    removeEventLocally,
    updateEventLocally
  } = useScheduleQueries();

  // Combinar eventos do servidor com eventos locais
  const combinedEvents = [...(trainerEvents || []), ...localEvents];
  
  const handleDateSelect = (date: Date) => {
    setSelectedDate(date);
    setShowRequestDialog(true);
  };
  
  const handleEventClick = (event: StudentEvent) => {
    setSelectedEvent(event);
    
    // Check if this is a student-created event that can be cancelled
    // or a trainer-created event that should only be viewed
    if (event.isCurrentStudent && 
        (event.type === 'request' || 
         event.status === 'pending_cancellation')) {
      setShowCancelDialog(true);
    } else {
      // For trainer-created events, show the details view
      setShowDetailsDialog(true);
    }
  };
  
  const handleRequestSuccess = (newEvent: StudentEvent) => {
    // Adicionar o novo evento aos eventos locais para exibição imediata
    setLocalEvents(prev => [...prev, newEvent]);
  };
  
  const handleCancelSuccess = (cancelledEvent: StudentEvent) => {
    // Para atualizações no UI imediatas, remover eventos rejeitados/cancelados
    if (cancelledEvent.status === 'cancelled' || cancelledEvent.status === 'rejected') {
      // Remover do estado local
      setLocalEvents(prev => prev.filter(e => e.id !== cancelledEvent.id));
      
      // Também remover do estado do hook useScheduleQueries
      if (removeEventLocally) {
        removeEventLocally(cancelledEvent.id);
      }
    } else {
      // Se o status for outro, atualizar o evento localmente
      setLocalEvents(prev => prev.map(e => e.id === cancelledEvent.id ? cancelledEvent : e));
      
      // Também atualizar no estado do hook useScheduleQueries
      if (updateEventLocally) {
        updateEventLocally(cancelledEvent);
      }
    }
  };
  
  if (!student?.trainer_id) {
    return <div className="flex items-center justify-center h-[400px]">
        <p className="text-muted-foreground dark:text-gray-400">
          {t("studentSchedule.trainerNotFound")}
        </p>
      </div>;
  }
  
  if (!settings) {
    return <div className="flex items-center justify-center h-[400px]">
        <p className="text-muted-foreground dark:text-gray-400">
          {t("studentSchedule.loadingSettings")}
        </p>
      </div>;
  }
  
  return <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 pb-4 border-b border-slate-100/50 dark:border-gray-700/50">
        <div className="space-y-2">
          <h2 className="text-3xl font-bold tracking-tight gradient-text">{t('title')}</h2>
          <p className="text-muted-foreground dark:text-gray-400">{t('subtitle')}</p>
          <Breadcrumb className="flex-wrap">
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href={`/${language === 'pt' ? 'pt/' : ''}student-dashboard`} className="text-flame hover:text-flame/80">{t('dashboard')}</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage className="font-medium dark:text-white">{t('title')}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
      </div>

      <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-lg rounded-xl p-4 shadow-md border border-slate-100/50 dark:border-gray-700/50 hover:shadow-lg transition-all">
        {isLoading ? <ScheduleSkeleton /> : <StudentCalendarViews view={'week'} date={date} settings={settings} events={combinedEvents} handleDateSelect={handleDateSelect} handleEventClick={handleEventClick} setDate={setDate} />}
      </div>

      {showRequestDialog && selectedDate && <RequestScheduleDialog isOpen={showRequestDialog} onClose={() => setShowRequestDialog(false)} date={selectedDate} onSuccess={handleRequestSuccess} />}

      {showCancelDialog && selectedEvent && <CancelScheduleDialog isOpen={showCancelDialog} onClose={() => {
      setShowCancelDialog(false);
      setSelectedEvent(null);
    }} event={selectedEvent} onSuccess={handleCancelSuccess} />}

      {showDetailsDialog && selectedEvent && <EventDetailsDialog isOpen={showDetailsDialog} onClose={() => {
      setShowDetailsDialog(false);
      setSelectedEvent(null);
    }} event={selectedEvent} />}
    </div>;
};
